import { isValid, parseISO } from 'date-fns';

export interface WaterBalanceSeriesPoint {
  date: string;
  value: number;
}

export interface WaterBalanceSeries {
  label?: string;
  name: string;
  data: WaterBalanceSeriesPoint[];
}

export interface WaterBalanceSeriesResponse {
  series: WaterBalanceSeries[];
  /** Rodada da previsão usada na série ('YYYY-MM-DD'); null em consulta só histórica. */
  runDate: string | null;
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;
const COMPACT_DAY = /^\d{8}$/;

// Normaliza 'YYYYMMDD' e 'YYYY-MM-DD' para 'YYYY-MM-DD'. Devolve '' quando não é um dia real
// ('2026-13-45', 'null'...): o date-fns lança RangeError ao formatar data inválida, e isso
// derrubava o card inteiro durante a renderização.
function toIsoDay(raw: string): string {
  const iso = COMPACT_DAY.test(raw) ? `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}` : raw;
  return ISO_DAY.test(iso) && isValid(parseISO(iso)) ? iso : '';
}

// Aceita 'YYYY-MM-DD' (contrato combinado com o backend) e 'YYYYMMDD' (o formato
// das `datas` da própria série). Qualquer outra coisa, ou um dia que não existe,
// vira "sem data da rodada".
export function parseRunDate(raw: unknown): string | null {
  return typeof raw === 'string' ? toIsoDay(raw) || null : null;
}

// A série temporal do BH vem como colunas: `datas` + uma lista por variável, ao
// lado de metadados (ex.: `data_rodada`). Só lista vira série; o resto é ignorado,
// para um campo novo do backend não derrubar o gráfico.
export default function toWaterBalanceSeries(
  data: Record<string, unknown>,
  isIgnoredVariable: (variable: string) => boolean = () => false,
): WaterBalanceSeriesResponse {
  const dates = Array.isArray(data.datas) ? data.datas.map((d) => toIsoDay(String(d))) : [];

  const series = Object
    .entries(data)
    .filter((entry): entry is [string, number[]] => (
      entry[0] !== 'datas' && Array.isArray(entry[1]) && !isIgnoredVariable(entry[0])
    ))
    .map(([variable, values]) => ({
      name: variable,
      data: values.map((value, i) => ({ date: dates[i], value })),
    }));

  return { series, runDate: parseRunDate(data.data_rodada) };
}
